import React from 'react'
import classNames from 'classnames'
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Alert, Col, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import Cookies from 'js-cookie'
import Loader from 'components/ui/loader/Loader'
import { useFullscreenStatus } from 'helpers'
import { useScormLoadPercentage } from 'helpers/scormLoadPercentage'
import { BlockScormContent } from 'store/states/lessons.state'
import { createRouteScorm } from 'enum/routes'
import { useIsMobile } from 'hooks/useIsMobile'
import BlockBanner from 'components/reusables/blockBanner'
import { BlockStatus } from 'enum/block-status.enum'
import { scormService } from 'api/lessons/block-instances/scorm'
import { initializeScorm } from './initializeScormUserData'
import styles from './Scorm.module.scss'
import { Buttons } from './Buttons'

class ScormUpdateQueue {
  private queue: (() => Promise<void>)[] = []
  private isProcessing = false

  enqueue(fn: () => Promise<void>) {
    this.queue.push(fn)
    if (!this.isProcessing) this.processQueue()
  }

  private async processQueue() {
    this.isProcessing = true
    while (this.queue.length > 0) {
      const task = this.queue.shift()
      if (task) await task()
    }
    this.isProcessing = false
  }
}

interface IScorm {
  data: BlockScormContent
  reload?: () => void
  buttons?: boolean
  showContent?: boolean
  canCloseWindow?: boolean
  next?(): void
}

interface IScormProps {
  API: {
    cmi: {
      suspend_data: string
      core: {
        lesson_location: string
      }
    }
  }
  API_1484_11: {
    cmi: {
      location: string
      suspend_data: string
    }
  }
}

const Scorm: React.FC<IScorm> = ({
  data: {
    id,
    fileData,
    url,
    processed,
    isValidated,
    loadPercentage,
    type,
    processing,
    openNewWindow,
    blockAdvance,
  },
  reload,
  buttons = true,
  canCloseWindow,
}) => {
  const [scormLoaded, setScormLoaded] = React.useState(false)
  const [scormStatus, setScormStatus] = React.useState(BlockStatus.Pending)
  const scormQueue = new ScormUpdateQueue()
  const { showPercentage, percentage } = useScormLoadPercentage({
    block: { id, loadPercentage, type, processing },
    onFinish: () => {
      reload?.()
    },
  })
  const isMobile = useIsMobile()
  const isAppMobile = Cookies.getJSON('rankmi_flavour_mobile')
  const definedUrl = isAppMobile ? `${url}?fullscreen=true` : url
  const target = isAppMobile ? '_self' : '_blank'

  React.useEffect(() => {
    const close = window.close
    if (!canCloseWindow)
      window.close = () => {
        // This is a hack to prevent the SCORM from closing the window.
      }
    return () => {
      window.close = close
    }
  }, [canCloseWindow])

  React.useEffect(() => {
    import('libs/scorm')
      .then(async ({ ScormAPI, ScormAPI2004 }) => {
        const API = ScormAPI()
        const API_1484_11 = ScormAPI2004()

        await initializeScorm({ API_1484_11, API })

        await getScormStatus({ API, API_1484_11 })

        API.on('LMSSetValue', onSetValue)
        API_1484_11.on('SetValue', onSetValue)

        Object.assign(window, { API_1484_11, API })
      })
      .finally(() => setScormLoaded(true))
    return () => {
      delete window.API_1484_11
      delete window.API
    }
  }, [])

  const getScormStatus = async ({ API, API_1484_11 }: IScormProps) => {
    await scormService.getScormStatus(id).then((response) => {
      setScormStatus(response.data.scormStatus)

      if (response.data.scormLocation !== '') {
        API.cmi.core.lesson_location = response.data.scormLocation
        API_1484_11.cmi.location = response.data.scormLocation
      }

      if (response.data.scormSuspendData !== '') {
        API.cmi.suspend_data = response.data.scormSuspendData
        API_1484_11.cmi.suspend_data = response.data.scormSuspendData
      }
    })
  }

  const onSetValue = (key: string, value: string) => {
    console.log('SCORM Event', key, value)

    switch (key) {
      case 'cmi.core.lesson_location':
      case 'cmi.location':
        saveLocation(value)
        break
      case 'cmi.suspend_data':
        saveSuspendData(value)
        break
      case 'cmi.core.lesson_status':
      case 'cmi.completion_status':
      case 'cmi.success_status':
        saveStatus(value)
        break
    }
  }

  const saveLocation = (location: string) => {
    scormQueue.enqueue(async () => {
      await scormService.saveLocation(id, location)
      setScormStatus((prev) => {
        if (prev === BlockStatus.Pending) {
          return BlockStatus.InProgress
        }
        return prev
      })
    })
  }

  const saveSuspendData = (suspendData: string) => {
    scormQueue.enqueue(async () => {
      await scormService.saveSuspendData(id, suspendData)
      setScormStatus((prev) => {
        if (prev === BlockStatus.Pending) {
          return BlockStatus.InProgress
        }
        return prev
      })
    })
  }

  const saveStatus = (status: string) => {
    if (['completed', 'passed'].includes(status)) {
      setScormStatus((prev) => {
        if (prev !== BlockStatus.Finish) {
          scormQueue.enqueue(async () => {
            await scormService.saveStatus(id, BlockStatus.Finish)
          })
          return BlockStatus.Finish
        }
        return prev
      })
    }
  }

  const { t } = useTranslation()

  const iframeRef = React.useRef<HTMLIFrameElement>(null)

  const [isFullscreen, setFullscreen] = useFullscreenStatus(iframeRef)

  const content = React.useMemo(() => {
    if (
      !showPercentage &&
      processed &&
      isValidated &&
      fileData &&
      fileData.id
    ) {
      if (!scormLoaded) return <Loader />
      return (
        <iframe
          className={styles.scorm}
          src={definedUrl}
          height="100%"
          width="100%"
          allowFullScreen={true}
        />
      )
    } else {
      const text = !processed || showPercentage ? 'processed' : 'isValidated'
      return (
        <Alert variant="secondary" className={styles.withErrors}>
          <FontAwesomeIcon icon={faInfoCircle}></FontAwesomeIcon>
          <p>
            {t(
              `lessons.contentBlocks.scormBlock.errors.${
                (!fileData?.id && 'emptyScorm') || text
              }`
            )}
          </p>
        </Alert>
      )
    }
  }, [
    showPercentage,
    processed,
    isValidated,
    fileData,
    scormLoaded,
    definedUrl,
    t,
  ])

  return (
    <Row
      ref={iframeRef}
      noGutters
      className="align-items-center align-content-center bg-white"
    >
      <Col xs="12">
        {blockAdvance && <BlockBanner status={scormStatus} />}
        <div
          className={classNames(
            !showPercentage && styles.scormContainer,
            isFullscreen && styles.fullScreen
          )}
        >
          {content}
          {showPercentage && percentage !== undefined && (
            <Col xs={12} className="px-0 mt-2">
              <div className="progress">
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  style={{
                    width: `${percentage}%`,
                    minWidth: '30px',
                  }}
                >
                  {percentage}%
                </div>
              </div>
            </Col>
          )}
        </div>
      </Col>
      {buttons && !showPercentage && (
        <Col xs="12">
          <Row className="justify-content-center">
            <Col xs="auto">
              <Buttons
                isFullscreen={isFullscreen}
                onClick={() => {
                  if (openNewWindow || isAppMobile || isMobile) {
                    const to = createRouteScorm({ id: id.toString() })
                    window.open(
                      `${to}?fullscreen=true`,
                      target,
                      'toolbar=0,location=0,menubar=0'
                    )
                  } else setFullscreen(!isFullscreen)
                }}
              />
            </Col>
          </Row>
        </Col>
      )}
    </Row>
  )
}

export default Scorm
